import { CaretLeft } from '@phosphor-icons/react';
import { AnimatePresence, motion } from 'framer-motion';

import { WizardSegmentedProgress } from './WizardSegmentedProgress';
import { useMobilePageHeaderBack } from '../../store/MobilePageHeaderContext';
import { cn } from '../../utils/cn';
import pt from '../../locales/pt';

const SLIDE_OFFSET = 26;
const SLIDE_TRANSITION = {
  duration: 0.34,
  ease: [0.22, 0.61, 0.36, 1],
};

/**
 * Molde partilhado de wizard — `.pen` `ds/wizard/container--bottom-sheet`
 * (BnyXH, mobile) e `--dialog` (AY8GK, md+): superfície `$surface-card`,
 * `rounded-$radius-ds-xl`, `$glass-border` + sombra `$glass-shadow`,
 * `padding [$space-3, $space-5, $space-5, $space-5]`, `gap $space-4` entre
 * cabeçalho / corpo / rodapé. O rodapé traz a sua própria linha superior
 * (`ds/wizard/footer`).
 *
 * Os wizards de domínio mantêm o estado do passo e compõem este shell.
 *
 * @param {boolean} [appChrome=false] Quando true (wizards no AppLayout): título/back só no
 *   MobileTopBar em mobile; shell preenche a altura do main para o CTA ficar sticky.
 */
export function WizardSkeleton({
  wizardTitle,
  step,
  total,
  onBack,
  showBack = true,
  direction = 'forward',
  loading = false,
  loadingLabel,
  showProgress = true,
  showPercent = true,
  /** Optional custom progress node (e.g. WizardPhasedProgress). */
  progress,
  footer,
  children,
  className,
  /** Extra classes on the top chrome (title + progress). */
  chromeClassName,
  /** Extra classes on the scrollable body. */
  bodyClassName,
  /** Extra classes on the footer wrapper. */
  footerClassName,
  appChrome = false,
}) {
  const copy = pt.wizard.common;
  const slideX = direction === 'back' ? -SLIDE_OFFSET : SLIDE_OFFSET;
  const shellBack = showBack && typeof onBack === 'function' ? onBack : null;

  useMobilePageHeaderBack(appChrome ? shellBack : null);

  return (
    <div
      data-ds-id="ds/wizard/container--dialog"
      className={cn(
        'relative mx-auto flex w-full max-w-lg flex-col overflow-hidden bg-background',
        'md:min-h-0 md:rounded-ds-xl md:border md:border-glass-border md:bg-card md:shadow-[0_12px_40px] md:shadow-glass-shadow',
        appChrome
          ? '-my-6 h-[calc(100dvh-3.5rem-5rem)] md:my-0 md:h-auto md:min-h-0'
          : 'min-h-[calc(100dvh-4rem)]',
        className,
      )}
    >
      <div
        className={cn(
          'flex shrink-0 flex-col gap-space-4 px-space-5 pb-space-4 pt-space-3',
          chromeClassName,
        )}
      >
        {onBack || wizardTitle ? (
          <div
            className={cn(
              'min-h-9 items-center gap-space-3',
              appChrome ? 'hidden md:flex' : 'flex',
            )}
          >
            {showBack && onBack ? (
              <button
                type="button"
                onClick={onBack}
                disabled={loading}
                className="grid size-10 shrink-0 place-items-center rounded-ds-md border border-border text-muted-foreground transition hover:bg-muted hover:text-foreground disabled:cursor-default disabled:opacity-30"
                aria-label={pt.common.back}
              >
                <CaretLeft className="size-5" weight="regular" aria-hidden />
              </button>
            ) : null}
            <span
              className={cn(
                'min-w-0 flex-1 truncate type-title-card tracking-tight text-card-foreground',
                appChrome && 'hidden md:inline',
              )}
            >
              {wizardTitle}
            </span>
          </div>
        ) : null}

        {showProgress
          ? progress != null
            ? progress
            : (
              <WizardSegmentedProgress step={step} total={total} showPercent={showPercent} />
            )
          : null}
      </div>

      <div
        className={cn(
          'flex min-h-0 flex-1 flex-col overflow-y-auto px-space-5 pb-space-2 pt-space-1',
          bodyClassName,
        )}
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={`${step}-${direction}`}
            initial={{ opacity: 0, x: slideX }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -slideX * 0.4 }}
            transition={SLIDE_TRANSITION}
            className="flex min-h-0 flex-1 flex-col gap-space-4"
          >
            {children}
          </motion.div>
        </AnimatePresence>
      </div>

      {footer ? (
        <div
          className={cn(
            'shrink-0 bg-background px-space-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] md:bg-card',
            footerClassName,
          )}
        >
          {footer}
        </div>
      ) : null}

      {loading ? (
        <div
          className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-space-4 bg-background/90 backdrop-blur-[2px] dark:bg-background/85"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <div
            className="size-11 animate-spin rounded-ds-full border-4 border-sage-100 border-t-sage-600 dark:border-sage-900 dark:border-t-sage-400"
            aria-hidden
          />
          <p className="m-0 text-type-body-sm font-semibold text-muted-foreground">
            {loadingLabel || copy.loadingDefault}
          </p>
        </div>
      ) : null}
    </div>
  );
}

export default WizardSkeleton;
